import { computed, readonly, ref } from "vue";

import { clearPersonalStorage } from "../utils/personal-storage";
import type { AccessRole, CurrentUser } from "../api/types";
import {
  fetchAuthMethods,
  fetchCurrentUser,
  signOut as signOutRequest,
  type AuthMethods,
} from "../api/client";

/**
 * The signed-in person, shared across the app (CTF-9303).
 *
 * Module-level refs on purpose: identity is one value per browser tab, not per
 * component, and every personal view needs the same answer. It stays outside
 * vue-query because the session cookie already is the cache — there is no
 * staleness to reconcile, only sign-in and sign-out.
 */

const user = ref<CurrentUser | null>(null);
/**
 * The viewer's role (CTF-9331). Null while signed out. The server resolves it
 * per request, so this is only what the UI uses to decide which controls to
 * render — every admin route enforces the same rule again.
 */
const role = ref<AccessRole | null>(null);
const loading = ref(false);
/** Null until the first /api/auth/me settles, so the UI can avoid flicker. */
const resolved = ref(false);
const methods = ref<AuthMethods>({
  oauth: false,
  token: false,
  required: false,
});

let inFlight: Promise<void> | null = null;

/** Loads identity once per page. Concurrent callers share the same request. */
export function loadCurrentUser(): Promise<void> {
  if (inFlight) return inFlight;
  loading.value = true;
  inFlight = Promise.all([
    fetchCurrentUser().catch(() => null),
    fetchAuthMethods().catch((): AuthMethods => ({
      oauth: false,
      token: false,
      required: false,
    })),
  ])
    .then(([found, nextMethods]) => {
      user.value = found?.user ?? null;
      role.value = found?.role ?? null;
      methods.value = nextMethods;
    })
    .finally(() => {
      loading.value = false;
      resolved.value = true;
      inFlight = null;
    });
  return inFlight;
}

export function useCurrentUser() {
  return {
    user: readonly(user),
    role: readonly(role),
    isAdmin: computed(() => role.value === "admin"),
    loading: readonly(loading),
    resolved: readonly(resolved),
    methods: readonly(methods),
    loadCurrentUser,

    async signOut(): Promise<void> {
      await signOutRequest();
      user.value = null;
      role.value = null;
      // The session is gone; what this person searched for should go with it.
      clearPersonalStorage();
    },
  };
}
